/**
 * DemoPage - Public Demo Page
 *
 * Public route at `/demo/:token` that loads demo configuration
 * and renders the DemoChatInterface. No authentication required.
 *
 * Validates: Requirements 16.3, 19.4, 18.5
 * - 16.3: THE Public_Demo_Link SHALL be accessible without authentication.
 * - 19.4: IF a Public_Demo_Link is disabled or expired, THEN THE Demo_Chat_Interface
 *   SHALL display a friendly "demo unavailable" message.
 * - 18.5: THE Demo_Chat_Interface SHALL display an optional "Powered by" footer.
 */

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DemoChatInterface, type DemoConfig } from '../demo/DemoChatInterface';

interface DemoData {
  agentName: string;
  config: DemoConfig;
  maxConversationLength: number;
  isValid: boolean;
  // Message limit fields from GET /demo/:token
  messageLimit?: number;
  messagesUsed?: number;
  messagesRemaining?: number;
  daysRemaining?: number;
  extensionAvailable?: boolean;
  operatorTest?: boolean;
}

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: DemoData };

export function DemoPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isLandingSession = searchParams.get('from') === 'landing';
  const freshSessionKey = searchParams.get('fresh');
  const operatorTestPass = searchParams.get('test');
  const [state, setState] = useState<PageState>({ status: 'loading' });

  // Ensure proper mobile viewport behavior (prevent zoom on input focus, handle safe areas)
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
    }
    // Prevent body scroll on mobile (chat handles its own scroll)
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    return () => {
      if (meta) {
        meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'Demo no disponible' });
      return;
    }

    let cancelled = false;

    async function loadDemo() {
      try {
        const testQuery = operatorTestPass ? `?test=${encodeURIComponent(operatorTestPass)}` : '';
        const response = await fetch(`/demo/${token}${testQuery}`);
        const json = await response.json();

        if (cancelled) return;

        if (!response.ok || !json.success) {
          const msg =
            json.error?.message ?? 'Esta demo no está disponible actualmente';
          setState({ status: 'error', message: msg });
          return;
        }

        setState({ status: 'ready', data: json.data });
      } catch {
        if (!cancelled) {
          setState({
            status: 'error',
            message: 'Error de conexión. Intenta de nuevo más tarde.',
          });
        }
      }
    }

    loadDemo();
    return () => {
      cancelled = true;
    };
  }, [token, operatorTestPass]);

  return (
    <>
      {state.status === 'loading' ? (
        <LoadingView />
      ) : state.status === 'error' ? (
        <ErrorView message={state.message} />
      ) : (
        <DemoChatInterface
          key={freshSessionKey ? `fresh-${freshSessionKey}` : 'session'}
          config={state.data.config}
          token={token!}
          maxConversationLength={state.data.maxConversationLength}
          isLandingSession={isLandingSession}
          freshSessionKey={freshSessionKey}
          operatorTest={state.data.operatorTest === true}
          operatorTestPass={operatorTestPass}
          initialLimitInfo={
            state.data.messageLimit !== undefined
              ? {
                  messagesUsed: state.data.messagesUsed ?? 0,
                  messageLimit: state.data.messageLimit,
                  messagesRemaining: state.data.messagesRemaining ?? state.data.messageLimit,
                  daysRemaining: state.data.daysRemaining ?? 0,
                  extensionAvailable: state.data.extensionAvailable ?? true,
                }
              : undefined
          }
        />
      )}
    </>
  );
}

// ============================================
// Sub-components
// ============================================

function LoadingView() {
  return (
    <div style={centeredContainerStyle}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={loadingCardStyle}>
        <div style={spinnerStyle} />
        <p style={{ margin: '16px 0 0', color: '#64748b', fontSize: '14px' }}>
          Cargando demo...
        </p>
      </div>
    </div>
  );
}

function ErrorView({ message }: { message: string }) {
  return (
    <div style={centeredContainerStyle}>
      <div style={errorCardStyle}>
        <div style={errorIconStyle}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 style={{ margin: '16px 0 8px', fontSize: '18px', color: '#1e293b' }}>
          Demo no disponible
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '14px', textAlign: 'center' }}>
          {message}
        </p>
      </div>
    </div>
  );
}

// ============================================
// Styles
// ============================================

const centeredContainerStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  background: '#f1f5f9',
};

const loadingCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  background: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
};

const spinnerStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid #e2e8f0',
  borderTopColor: '#7c3aed',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const errorCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  background: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  maxWidth: '400px',
  width: '100%',
};

const errorIconStyle: CSSProperties = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  background: '#fee2e2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
